<script setup>
import { ref, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Icon from 'next/icon/Icon.vue';
import { uniqueSidebarNames } from 'dashboard/helper/sidebarCustomization';

const props = defineProps({
  open: { type: Boolean, default: false },
  sections: { type: Array, default: () => [] },
  favoriteNames: { type: Array, default: () => [] },
  collapsedSectionNames: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const draftFavorites = ref([]);
const draftCollapsedSections = ref([]);
const draftSections = ref([]);

watch(
  () => props.open,
  isOpen => {
    if (!isOpen) return;
    draftFavorites.value = uniqueSidebarNames(props.favoriteNames);
    draftCollapsedSections.value = uniqueSidebarNames(
      props.collapsedSectionNames
    ).filter(name => name !== 'section-favorites');
    draftSections.value = props.sections.map(section => ({
      ...section,
      items: [...section.items],
    }));
  },
  { immediate: true }
);

const toggleValue = (list, value) =>
  list.includes(value) ? list.filter(item => item !== value) : [...list, value];

const toggleFavorite = itemName => {
  draftFavorites.value = toggleValue(draftFavorites.value, itemName);
};

const toggleCollapsedSection = sectionName => {
  draftCollapsedSections.value = toggleValue(
    draftCollapsedSections.value,
    sectionName
  );
};

const save = () => {
  emit('save', {
    favorites: uniqueSidebarNames(draftFavorites.value),
    collapsedSections: uniqueSidebarNames(draftCollapsedSections.value),
    sectionOrder: draftSections.value.map(section => section.name),
    itemOrder: draftSections.value.flatMap(section =>
      section.items.map(item => item.name)
    ),
  });
};

const findItem = itemName =>
  draftSections.value
    .flatMap(section => section.items)
    .find(item => item.name === itemName);

useEventListener(document, 'keydown', event => {
  if (props.open && event.key === 'Escape') emit('close');
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 p-4 backdrop-blur-md"
      role="presentation"
      @click.self="emit('close')"
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="sidebar-customization-title"
        class="flex max-h-[min(50rem,calc(100vh-2rem))] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-2xl"
      >
        <header
          class="flex items-start justify-between gap-4 border-b border-n-weak px-6 py-5"
        >
          <div>
            <h2
              id="sidebar-customization-title"
              class="text-lg font-semibold text-n-slate-12"
            >
              {{ t('SIDEBAR.CUSTOMIZE.TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-10">
              {{ t('SIDEBAR.CUSTOMIZE.DESCRIPTION') }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-8 shrink-0 place-content-center rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12"
            :aria-label="t('SIDEBAR.CUSTOMIZE.CLOSE')"
            @click="emit('close')"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </header>

        <div class="min-h-0 overflow-y-auto px-6 py-5">
          <div
            class="grid items-start gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(17rem,1fr)]"
          >
            <div>
              <p class="mb-3 flex items-center gap-2 text-sm text-n-slate-10">
                <Icon icon="i-lucide-grip-vertical" class="size-4" />
                {{ t('SIDEBAR.CUSTOMIZE.REORDER_HINT') }}
              </p>
              <Draggable
                v-model="draftSections"
                item-key="name"
                handle=".sidebar-section-drag-handle"
                animation="180"
                ghost-class="opacity-40"
                class="grid gap-4 md:grid-cols-2"
              >
                <template #item="{ element: section }">
                  <article
                    class="overflow-hidden rounded-xl border border-n-weak bg-n-background"
                  >
                    <div
                      class="flex items-center justify-between gap-3 border-b border-n-weak px-3 py-3"
                    >
                      <div class="flex min-w-0 items-center gap-2">
                        <span
                          v-tooltip.top="t('SIDEBAR.CUSTOMIZE.DRAG_GROUP')"
                          class="sidebar-section-drag-handle grid size-7 shrink-0 cursor-grab place-content-center rounded-md text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-11 active:cursor-grabbing"
                        >
                          <Icon icon="i-lucide-grip-vertical" class="size-4" />
                        </span>
                        <div class="min-w-0">
                          <h3
                            class="truncate text-sm font-semibold text-n-slate-12"
                          >
                            {{ section.label }}
                          </h3>
                          <span class="text-xs text-n-slate-9">
                            {{
                              t('SIDEBAR.CUSTOMIZE.ITEM_COUNT', {
                                count: section.items.length,
                              })
                            }}
                          </span>
                        </div>
                      </div>
                      <label
                        class="flex shrink-0 items-center gap-2 text-xs text-n-slate-10"
                      >
                        <span class="hidden xl:inline">{{
                          t('SIDEBAR.CUSTOMIZE.START_COLLAPSED')
                        }}</span>
                        <input
                          type="checkbox"
                          class="size-4 accent-n-brand"
                          :checked="
                            draftCollapsedSections.includes(section.name)
                          "
                          @change="toggleCollapsedSection(section.name)"
                        />
                      </label>
                    </div>

                    <Draggable
                      :list="section.items"
                      item-key="name"
                      handle=".sidebar-item-drag-handle"
                      animation="180"
                      ghost-class="opacity-40"
                      tag="ul"
                      class="m-0 grid min-h-12 list-none gap-1 p-2"
                    >
                      <template #item="{ element: item }">
                        <li
                          class="flex h-11 items-center gap-2 rounded-lg px-2 text-sm transition hover:bg-n-alpha-2"
                          :class="
                            draftFavorites.includes(item.name)
                              ? 'bg-n-brand/10 text-n-slate-12'
                              : 'text-n-slate-11'
                          "
                        >
                          <span
                            v-tooltip.top="t('SIDEBAR.CUSTOMIZE.DRAG_ITEM')"
                            class="sidebar-item-drag-handle grid size-6 shrink-0 cursor-grab place-content-center text-n-slate-8 active:cursor-grabbing"
                          >
                            <Icon
                              icon="i-lucide-grip-vertical"
                              class="size-4"
                            />
                          </span>
                          <Icon
                            :icon="item.icon || 'i-lucide-circle'"
                            class="size-4 shrink-0"
                          />
                          <span class="min-w-0 flex-1 truncate">{{
                            item.label
                          }}</span>
                          <button
                            v-tooltip.top="
                              draftFavorites.includes(item.name)
                                ? t('SIDEBAR.CUSTOMIZE.REMOVE_FAVORITE')
                                : t('SIDEBAR.CUSTOMIZE.ADD_FAVORITE')
                            "
                            type="button"
                            class="grid size-8 shrink-0 place-content-center rounded-md hover:bg-n-alpha-3"
                            :aria-label="
                              draftFavorites.includes(item.name)
                                ? t('SIDEBAR.CUSTOMIZE.REMOVE_FAVORITE')
                                : t('SIDEBAR.CUSTOMIZE.ADD_FAVORITE')
                            "
                            @click="toggleFavorite(item.name)"
                          >
                            <Icon
                              :icon="
                                draftFavorites.includes(item.name)
                                  ? 'i-ri-star-fill'
                                  : 'i-lucide-star'
                              "
                              class="size-4"
                              :class="{
                                'text-n-amber-9': draftFavorites.includes(
                                  item.name
                                ),
                              }"
                            />
                          </button>
                        </li>
                      </template>
                    </Draggable>
                  </article>
                </template>
              </Draggable>
            </div>

            <aside
              class="rounded-xl border border-n-brand/25 bg-n-brand/5 p-4 lg:sticky lg:top-0"
            >
              <div class="flex items-start gap-3">
                <span
                  class="grid size-9 shrink-0 place-content-center rounded-lg bg-n-brand/15 text-n-brand"
                >
                  <Icon icon="i-lucide-bookmark-check" class="size-5" />
                </span>
                <div>
                  <h3 class="font-semibold text-n-slate-12">
                    {{ t('SIDEBAR.CUSTOMIZE.FAVORITES_TITLE') }}
                  </h3>
                  <p class="mt-1 text-xs leading-5 text-n-slate-10">
                    {{ t('SIDEBAR.CUSTOMIZE.FAVORITES_DESCRIPTION') }}
                  </p>
                </div>
              </div>

              <Draggable
                v-if="draftFavorites.length"
                v-model="draftFavorites"
                :item-key="name => name"
                animation="180"
                ghost-class="opacity-40"
                class="mt-4 grid gap-2"
              >
                <template #item="{ element: itemName }">
                  <div
                    v-if="findItem(itemName)"
                    v-tooltip.top="t('SIDEBAR.CUSTOMIZE.DRAG_FAVORITE')"
                    class="flex h-11 cursor-grab items-center gap-2 rounded-lg border border-n-brand/20 bg-n-solid-2 px-3 text-sm text-n-slate-12 shadow-sm active:cursor-grabbing"
                  >
                    <Icon
                      icon="i-lucide-grip-vertical"
                      class="size-4 text-n-slate-8"
                    />
                    <Icon
                      :icon="findItem(itemName).icon || 'i-lucide-circle'"
                      class="size-4 shrink-0"
                    />
                    <span class="min-w-0 flex-1 truncate">
                      {{ findItem(itemName).label }}
                    </span>
                    <button
                      v-tooltip.top="t('SIDEBAR.CUSTOMIZE.REMOVE_FAVORITE')"
                      type="button"
                      class="grid size-7 shrink-0 place-content-center rounded-md text-n-amber-9 hover:bg-n-alpha-2"
                      :aria-label="t('SIDEBAR.CUSTOMIZE.REMOVE_FAVORITE')"
                      @click="toggleFavorite(itemName)"
                    >
                      <Icon icon="i-ri-star-fill" class="size-4" />
                    </button>
                  </div>
                </template>
              </Draggable>
              <p
                v-else
                class="mt-4 rounded-lg border border-dashed border-n-weak px-4 py-6 text-center text-xs leading-5 text-n-slate-10"
              >
                {{ t('SIDEBAR.CUSTOMIZE.NO_FAVORITES') }}
              </p>
            </aside>
          </div>
        </div>

        <footer
          class="flex items-center justify-end gap-3 border-t border-n-weak px-6 py-4"
        >
          <button
            type="button"
            class="h-9 rounded-lg px-4 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            @click="emit('close')"
          >
            {{ t('SIDEBAR.CUSTOMIZE.CANCEL') }}
          </button>
          <button
            type="button"
            class="h-9 rounded-lg bg-n-brand px-4 text-sm font-medium text-white hover:opacity-90"
            @click="save"
          >
            {{ t('SIDEBAR.CUSTOMIZE.SAVE') }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
